import React, { useState, useCallback, useEffect, useRef } from 'react';
import Footer from '../layout/footer';
import NavigationBar from '../layout/NavigationBar';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';

const DropGeotagForm = () => {
    // Form state
    const [formData, setFormData] = useState({
        userId: '',
        location: '',
        latitude: '',
        longitude: '',
        text: '',
        geotags_description: '',
        businessId: '',
        geotag_type: 'Yourself', // Yourself or Business
        premium_status: false,
        treasure_status: true,
    });

    // Business state
    const [businesses, setBusinesses] = useState([]);
    const [loadingBusinesses, setLoadingBusinesses] = useState(false);
    const [businessError, setBusinessError] = useState('');

    // File handling state
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [geotagLogo, setGeotagLogo] = useState('');

    // UI state
    const [loading, setLoading] = useState(false);
    const [selectedCircles, setSelectedCircles] = useState([]);

    // Map state
    const [map, setMap] = useState(null);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [marker, setMarker] = useState(null);
    const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
    const [addingBusiness, setAddingBusiness] = useState(false);
    const [businessFormData, setBusinessFormData] = useState({
        business_name: '',
        mobile: '',
        business_logo: '',
        country_code: '+91',
        address1: '',
        address2: '',
        city: '',
        state: '',
        pincode: ''
    });

    const mapContainerStyle = {
        width: '100%',
        height: '400px',
        borderRadius: '6px',
        border: '1px solid #d1d5db'
    };

    const center = {
        lat: 11.0168, // Coimbatore coordinates
        lng: 76.9558
    };

    const mapOptions = {
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
    };


    // Handle map click - improved version
    const handleMapClick = useCallback(async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Update marker position
        setMarkerPosition({ lat, lng });

        // Update form data
        setFormData(prev => ({
            ...prev,
            latitude: lat.toString(),
            longitude: lng.toString()
        }));

        // Reverse geocoding to get address
        try {
            const geocoder = new window.google.maps.Geocoder();
            const response = await new Promise((resolve, reject) => {
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        resolve(results[0]);
                    } else {
                        reject(new Error('Geocoding failed'));
                    }
                });
            });

            if (response.formatted_address) {
                setFormData(prev => ({
                    ...prev,
                    location: response.formatted_address
                }));
            }
        } catch (error) {
            console.error('Error getting address:', error);
        }
    }, []);

    // Initialize map - improved version


    // Fetch business details when userId changes
    useEffect(() => {
        if (formData.userId && formData.userId.trim()) {
            fetchBusinessDetails(formData.userId);
        } else {
            setBusinesses([]);
            setBusinessError('');
        }
    }, [formData.userId]);

    // Fetch business details
    const fetchBusinessDetails = async (userId) => {
        if (!userId || !userId.trim()) return;

        setLoadingBusinesses(true);
        setBusinessError('');

        try {
            const response = await fetch(process.env.REACT_APP_APIURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    method: 'getBusinessDetails',
                    environment: "live",
                    module: "business",
                    userId: userId,
                    apikey: process.env.REACT_APP_APIKEY,
                    secret: process.env.REACT_APP_SECRET
                })
            });

            const data = await response.json();

            if (data.status === "success" && data.statusCode === 200) {
                setBusinesses(data.result.business_details || []);
                if (!data.result || data.result.length === 0) {
                    setBusinessError('No businesses found for this user');
                }
            } else {
                setBusinessError(data.message || 'Failed to fetch business details');
                setBusinesses([]);
            }
        } catch (error) {
            console.error('Error fetching business details:', error);
            setBusinessError('Error fetching business details');
            setBusinesses([]);
        } finally {
            setLoadingBusinesses(false);
        }
    };

    // Handle manual coordinate input - improved version
    const handleCoordinateChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Update marker position if both coordinates are valid
        if ((name === 'latitude' || name === 'longitude')) {
            const lat = name === 'latitude' ? parseFloat(value) : parseFloat(formData.latitude);
            const lng = name === 'longitude' ? parseFloat(value) : parseFloat(formData.longitude);

            if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                setMarkerPosition({ lat, lng });

                // Center map on new position
                if (map) {
                    map.panTo({ lat, lng });
                    map.setZoom(15);
                }
            }
        }
    };

    // Get current location - improved version
    const getCurrentLocation = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    // Update marker and form data
                    setMarkerPosition({ lat, lng });
                    setFormData(prev => ({
                        ...prev,
                        latitude: lat.toString(),
                        longitude: lng.toString()
                    }));

                    // Center map on current location
                    if (map) {
                        map.panTo({ lat, lng });
                        map.setZoom(15);
                    }

                    // Get address using reverse geocoding
                    try {
                        const geocoder = new window.google.maps.Geocoder();
                        const response = await new Promise((resolve, reject) => {
                            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                                if (status === 'OK' && results[0]) {
                                    resolve(results[0]);
                                } else {
                                    reject(new Error('Geocoding failed'));
                                }
                            });
                        });

                        if (response.formatted_address) {
                            setFormData(prev => ({
                                ...prev,
                                location: response.formatted_address
                            }));
                        }
                    } catch (error) {
                        console.error('Error getting address:', error);
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                    alert('Error getting your location. Please select manually on the map.');
                }
            );
        } else {
            alert('Geolocation is not supported by this browser.');
        }
    }, [map]);

    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    // Styles
    const styles = {
        container: {
            maxWidth: '1024px',
            margin: '0 auto',
            padding: '24px',
            backgroundColor: 'white',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            borderRadius: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        },
        title: {
            fontSize: '30px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '32px',
            textAlign: 'center'
        },
        formSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
        },
        gridRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column'
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px'
        },
        input: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            transition: 'all 0.2s',
            boxSizing: 'border-box'
        },
        textarea: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box'
        },
        select: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            backgroundColor: 'white',
            boxSizing: 'border-box'
        },
        fileInput: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            backgroundColor: 'white',
            boxSizing: 'border-box'
        },
        mapContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
        },
        mapSection: {
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#f9fafb'
        },
        mapHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            flexWrap: 'wrap',
            gap: '8px'
        },
        mapTitle: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#1f2937'
        },
        locationButton: {
            padding: '6px 12px',
            backgroundColor: '#10b981',
            color: 'white',
            fontSize: '12px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        mapElement: {
            width: '100%',
            height: '400px',
            borderRadius: '6px',
            border: '1px solid #d1d5db'
        },
        mapInstructions: {
            fontSize: '12px',
            color: '#6b7280',
            fontStyle: 'italic',
            marginTop: '8px'
        },
        mediaSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '8px'
        },
        progressContainer: {
            width: '100%',
            backgroundColor: '#e5e7eb',
            borderRadius: '9999px',
            height: '10px',
            overflow: 'hidden'
        },
        progressBar: {
            height: '100%',
            backgroundColor: '#2563eb',
            borderRadius: '9999px',
            transition: 'width 0.3s ease'
        },
        progressText: {
            fontSize: '14px',
            color: '#4b5563',
            marginTop: '4px'
        },
        filesList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        },
        fileItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px'
        },
        fileInfo: {
            flex: 1
        },
        fileType: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
        },
        captionInput: {
            width: '100%',
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            outline: 'none',
            boxSizing: 'border-box'
        },
        removeButton: {
            padding: '6px 12px',
            backgroundColor: '#ef4444',
            color: 'white',
            fontSize: '12px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        checkboxContainer: {
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap'
        },
        checkboxGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        checkbox: {
            width: '16px',
            height: '16px',
            accentColor: '#2563eb'
        },
        checkboxLabel: {
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer'
        },
        submitContainer: {
            paddingTop: '24px'
        },
        submitButton: {
            width: '100%',
            padding: '12px 16px',
            borderRadius: '6px',
            color: 'white',
            fontWeight: '500',
            fontSize: '16px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        submitButtonEnabled: {
            backgroundColor: '#2563eb'
        },
        submitButtonDisabled: {
            backgroundColor: '#9ca3af',
            cursor: 'not-allowed'
        },
        logoPreview: {
            marginTop: '8px'
        },
        logoImage: {
            width: '80px',
            height: '80px',
            objectFit: 'cover',
            borderRadius: '6px',
            border: '1px solid #d1d5db'
        },
        errorText: {
            fontSize: '12px',
            color: '#ef4444',
            marginTop: '4px'
        },
        loadingText: {
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '4px'
        },
        businessInfo: {
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '4px'
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'latitude' || name === 'longitude') {
            handleCoordinateChange(e);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    // Handle business selection
    const handleBusinessSelect = (e) => {
        const selectedBusinessId = e.target.value;
        setFormData(prev => ({
            ...prev,
            businessId: selectedBusinessId
        }));
    };

    // Upload file to S3 using presigned URL
    // Upload file to S3 using presigned URL - FIXED VERSION
    const uploadFileToS3 = async (file, fileType) => {
        setUploadProgress(0);
        setUploading(true);

        try {
            // Request presigned URL from backend
            const presignedUrlResponse = await fetch(process.env.REACT_APP_APIURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    method: 'getS3PresignedUrls',
                    environment: "live",
                    module: "geotag",
                    userId: formData.userId,
                    fileName: file.name || `file-${Date.now()}.${fileType.split('/')[1]}`,
                    fileType: fileType,
                    apikey: process.env.REACT_APP_APIKEY,
                    secret: process.env.REACT_APP_SECRET
                })
            });

            if (!presignedUrlResponse.ok) {
                throw new Error(`Failed to get presigned URL: ${presignedUrlResponse.status}`);
            }

            const presignedData = await presignedUrlResponse.json();

            if (!presignedData?.result) {
                throw new Error('Invalid presigned URL response');
            }

            const { url, fields, key, file_id } = presignedData.result;

            if (!url || !key) {
                throw new Error('Missing required S3 upload parameters');
            }

            // Create FormData for S3 upload
            const formDataS3 = new FormData();

            // Add fields in the correct order for S3
            if (fields) {
                // Add all fields except 'file' first
                Object.keys(fields).forEach(fieldKey => {
                    formDataS3.append(fieldKey, fields[fieldKey]);
                });
            }

            // Add key if not already in fields
            if (!fields?.key) {
                formDataS3.append('key', key);
            }

            // Add the file LAST - this is crucial for S3
            formDataS3.append('file', file);

            // Upload to S3 with CORS handling
            try {
                const uploadResponse = await fetch(url, {
                    method: 'POST',
                    body: formDataS3,
                    mode: 'cors', // Explicitly set CORS mode
                    // Don't set Content-Type header - let browser set it with boundary
                });

                // S3 returns 204 (No Content) for successful uploads
                if (!uploadResponse.ok && uploadResponse.status !== 204) {
                    const errorText = await uploadResponse.text();
                    console.error('S3 upload response:', errorText);
                    throw new Error(`S3 upload failed: ${uploadResponse.status} - ${errorText}`);
                }
            } catch (error) {
                // Handle CORS errors - S3 uploads often succeed even when CORS blocks the response
                if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
                    console.warn('CORS error detected, but upload may have succeeded. Proceeding with file URL construction.');
                    // Continue execution - the file was likely uploaded successfully
                } else {
                    throw error; // Re-throw other errors
                }
            }

            // Construct file URL - fix the URL construction
            const fileUrl = url.endsWith('/') ? `${url}${key}` : `${url}/${key}`;

            return {
                file_url: fileUrl,
                file_id: file_id
            };

        } catch (error) {
            console.error('File upload error:', error);

            // Handle specific error types
            if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
                // For CORS errors during development, show a helpful message
                if (window.location.hostname === 'localhost') {
                    alert('CORS error detected. This is common in development. The file may have uploaded successfully. Check your S3 bucket or try uploading from a deployed environment.');
                } else {
                    alert('Network error: Please check your internet connection and try again.');
                }
            } else if (error.message.includes('presigned URL')) {
                alert('Error getting upload URL. Please try again.');
            } else {
                alert(`Upload error: ${error.message}`);
            }

            throw error;
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Handle file selection and upload
    const handleFileUpload = async (e, fileType) => {
        const selectedFiles = Array.from(e.target.files);

        if (selectedFiles.length === 0) return;

        setUploading(true);

        try {
            const uploadPromises = selectedFiles.map(async (file, index) => {
                const uploadResult = await uploadFileToS3(file, file.type);
                return {
                    fileData: uploadResult.file_url,
                    file_type: fileType,
                    file_id: uploadResult.file_id,
                    caption: ''
                };
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            setFiles(prev => [...prev, ...uploadedFiles]);

        } catch (error) {
            console.error('Error uploading files:', error);
            alert('Error uploading files. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    // Handle logo upload
    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const uploadResult = await uploadFileToS3(file, file.type);
            setGeotagLogo(uploadResult.file_url);
        } catch (error) {
            console.error('Error uploading logo:', error);
            alert('Error uploading logo. Please try again.');
        }
    };

    // Update file caption
    const updateFileCaption = (index, caption) => {
        setFiles(prev => prev.map((file, i) =>
            i === index ? { ...file, caption } : file
        ));
    };

    // Remove file
    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Validate form
    const validateForm = () => {
        if (!formData.userId) {
            alert('User ID is required');
            return false;
        }
        if (!formData.location) {
            alert('Location is required');
            return false;
        }
        if (!formData.latitude || !formData.longitude) {
            alert('Latitude and Longitude are required');
            return false;
        }
        if (files.length === 0 && !formData.text) {
            alert('Please add at least one file or text content');
            return false;
        }
        return true;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        try {
            const payload = {
                environment: "live",
                module: "geotag",
                method: "dropGeotag",
                userId: formData.userId,
                location: formData.location,
                latitude: formData.latitude,
                longitude: formData.longitude,
                text: formData.text,
                geotags_description: formData.geotags_description,
                files: files,
                treasure_status: formData.treasure_status,
                premium_status: formData.premium_status,
                geotag_logo: geotagLogo,
                geotag_type: selectedCircles.length > 0 ? "circle" : formData.geotag_type,
                businessId: formData.businessId,
                circleId: selectedCircles,
                apikey: process.env.REACT_APP_APIKEY,
                secret: process.env.REACT_APP_SECRET
            };

            console.log('Payload:', payload);

            const response = await fetch(process.env.REACT_APP_APIURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === "success" && data.statusCode === 200) {
                alert('Geotag dropped successfully!');

                // Reset form
                setFormData({
                    userId: '',
                    location: '',
                    latitude: '',
                    longitude: '',
                    text: '',
                    geotags_description: '',
                    businessId: '',
                    geotag_type: 'Yourself',
                    premium_status: false,
                    treasure_status: true,
                });
                setFiles([]);
                setGeotagLogo('');
                setSelectedCircles([]);
                setBusinesses([]);
                setBusinessError('');

                // Clear map marker
                if (marker && map) {
                    map.removeLayer(marker);
                    setMarker(null);
                }
            } else {
                alert(data.message || 'Failed to drop Geotag');
            }

        } catch (error) {
            console.error('Drop Geotag error:', error);
            alert('Error dropping geotag. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const addBusiness = async () => {
        if (!businessFormData.business_name.trim()) {
            alert('Business name is required');
            return;
        }
        if (!businessFormData.mobile.trim()) {
            alert('Mobile number is required');
            return;
        }

        setAddingBusiness(true);
        try {
            const response = await fetch(process.env.REACT_APP_APIURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    method: 'addBusiness',
                    environment: "live",
                    module: "business",
                    userId: formData.userId,
                    business_name: businessFormData.business_name,
                    mobile: businessFormData.mobile,
                    business_logo: businessFormData.business_logo,
                    country_code: businessFormData.country_code,
                    address1: businessFormData.address1,
                    address2: businessFormData.address2,
                    city: businessFormData.city,
                    state: businessFormData.state,
                    pincode: businessFormData.pincode,
                    apikey: process.env.REACT_APP_APIKEY,
                    secret: process.env.REACT_APP_SECRET
                })
            });

            const data = await response.json();
            if (data.status === "success" && data.statusCode === 200) {
                alert('Business added successfully!');
                // Reset form
                setBusinessFormData({
                    business_name: '',
                    mobile: '',
                    business_logo: '',
                    country_code: '+91',
                    address1: '',
                    address2: '',
                    city: '',
                    state: '',
                    pincode: ''
                });
                // Close modal
                setShowAddBusinessModal(false);
                // Refresh business list
                fetchBusinessDetails(formData.userId);
            } else {
                alert(data.message || 'Failed to add business');
            }
        } catch (error) {
            console.error('Error adding business:', error);
            alert('Error adding business. Please try again.');
        } finally {
            setAddingBusiness(false);
        }
    };

    // 3. Add business form input handler

    const handleBusinessFormChange = (e) => {
        const { name, value } = e.target;
        setBusinessFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 4. Add business logo upload handler

    const handleBusinessLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const uploadResult = await uploadFileToS3(file, file.type);
            setBusinessFormData(prev => ({
                ...prev,
                business_logo: uploadResult.file_url
            }));
        } catch (error) {
            console.error('Error uploading business logo:', error);
            alert('Error uploading logo. Please try again.');
        }
    };

    const handleAddressInput = async (address) => {
        if (!address || address.trim().length < 3) return; // Don't geocode very short inputs

        try {
            const geocoder = new window.google.maps.Geocoder();
            const response = await new Promise((resolve, reject) => {
                geocoder.geocode({ address: address }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        resolve(results[0]);
                    } else {
                        reject(new Error('Geocoding failed'));
                    }
                });
            });

            if (response.geometry && response.geometry.location) {
                const lat = response.geometry.location.lat();
                const lng = response.geometry.location.lng();

                // Update form data with coordinates
                setFormData(prev => ({
                    ...prev,
                    latitude: lat.toString(),
                    longitude: lng.toString()
                }));

                // Update marker position
                setMarkerPosition({ lat, lng });

                // Center map on new position
                if (map) {
                    map.panTo({ lat, lng });
                    map.setZoom(15);
                }
            }
        } catch (error) {
            console.error('Error geocoding address:', error);
            // Don't show alert for every typing attempt, just log the error
        }
    };

    // Add debounce function to prevent too many API calls
    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    };

    // Add this at the top of your component with other state declarations
    const debouncedLocation = useDebounce(formData.location, 1000); // 1 second delay

    // Add this useEffect after your existing useEffects
    useEffect(() => {
        if (debouncedLocation && debouncedLocation.trim()) {
            handleAddressInput(debouncedLocation);
        }
    }, [debouncedLocation]);

    // 5. Add modal styles to your existing styles object

    const modalStyles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modal: {
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '12px'
        },
        modalTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px'
        },
        modalBody: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
        },
        modalButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
        },
        addButton: {
            padding: '8px 16px',
            backgroundColor: '#10b981',
            color: 'white',
            fontSize: '14px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        cancelButton: {
            padding: '8px 16px',
            backgroundColor: '#6b7280',
            color: 'white',
            fontSize: '14px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        submitButton: {
            padding: '8px 16px',
            backgroundColor: '#2563eb',
            color: 'white',
            fontSize: '14px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        disabledButton: {
            backgroundColor: '#9ca3af',
            cursor: 'not-allowed'
        }
    };

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>
            <div className='content-wrapper'>
                <div className="content">
                    <div className="container-fluid">
                        <div style={styles.container}>
                            <h1 style={styles.title}>Drop Geotag</h1>

                            <div style={styles.formSection}>
                                {/* Basic Information */}
                                {/* <div style={styles.gridRow}> */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        User ID *
                                    </label>
                                    <input
                                        type="text"
                                        name="userId"
                                        value={formData.userId}
                                        onChange={handleInputChange}
                                        style={styles.input}
                                        required
                                        placeholder="Enter User ID to load businesses"
                                    />
                                    {loadingBusinesses && (
                                        <p style={styles.loadingText}>Loading businesses...</p>
                                    )}
                                    {businessError && (
                                        <p style={styles.errorText}>{businessError}</p>
                                    )}
                                </div>

                                <div style={styles.inputGroup}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label style={styles.label}>
                                            Select Business (if Business type)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowAddBusinessModal(true)}
                                            style={modalStyles.addButton}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                                        >
                                            + Add Business
                                        </button>
                                    </div>
                                    <select
                                        name="businessId"
                                        value={formData.businessId}
                                        onChange={handleBusinessSelect}
                                        style={styles.select}
                                        disabled={!businesses.length || loadingBusinesses}
                                    >
                                        <option value="">
                                            {loadingBusinesses
                                                ? 'Loading businesses...'
                                                : businesses.length > 0
                                                    ? 'Select a business'
                                                    : 'No businesses available'
                                            }
                                        </option>
                                        {businesses.map((business) => (
                                            <option key={business._id || business.id} value={business._id || business.id}>
                                                {business.business_name || business.name || 'Unnamed Business'}
                                                {business.business_type && ` (${business.business_type})`}
                                            </option>
                                        ))}
                                    </select>
                                    {businesses.length > 0 && (
                                        <p style={styles.businessInfo}>
                                            {businesses.length} business{businesses.length !== 1 ? 'es' : ''} found
                                        </p>
                                    )}
                                </div>
                                {showAddBusinessModal && (
                                    <div style={modalStyles.overlay} onClick={(e) => {
                                        if (e.target === e.currentTarget) setShowAddBusinessModal(false);
                                    }}>
                                        <div style={modalStyles.modal}>
                                            <div style={modalStyles.modalHeader}>
                                                <h3 style={modalStyles.modalTitle}>Add New Business</h3>
                                                <button
                                                    style={modalStyles.closeButton}
                                                    onClick={() => setShowAddBusinessModal(false)}
                                                >
                                                    ×
                                                </button>
                                            </div>

                                            <div style={modalStyles.modalBody}>
                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Business Name *</label>
                                                    <input
                                                        type="text"
                                                        name="business_name"
                                                        value={businessFormData.business_name}
                                                        onChange={handleBusinessFormChange}
                                                        style={styles.input}
                                                        placeholder="Enter business name"
                                                        required
                                                    />
                                                </div>

                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Mobile Number *</label>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <select
                                                            name="country_code"
                                                            value={businessFormData.country_code}
                                                            onChange={handleBusinessFormChange}
                                                            style={{ ...styles.select, width: '100px' }}
                                                        >
                                                            <option value="+91">+91</option>
                                                            <option value="+1">+1</option>
                                                            <option value="+44">+44</option>
                                                            <option value="+971">+971</option>
                                                        </select>
                                                        <input
                                                            type="tel"
                                                            name="mobile"
                                                            value={businessFormData.mobile}
                                                            onChange={handleBusinessFormChange}
                                                            style={{ ...styles.input, flex: 1 }}
                                                            placeholder="Enter mobile number"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Business Logo</label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleBusinessLogoUpload}
                                                        style={styles.fileInput}
                                                    />
                                                    {businessFormData.business_logo && (
                                                        <div style={styles.logoPreview}>
                                                            <img
                                                                src={businessFormData.business_logo}
                                                                alt="Business logo preview"
                                                                style={styles.logoImage}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Address Line 1</label>
                                                    <input
                                                        type="text"
                                                        name="address1"
                                                        value={businessFormData.address1}
                                                        onChange={handleBusinessFormChange}
                                                        style={styles.input}
                                                        placeholder="Enter address line 1"
                                                    />
                                                </div>

                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Address Line 2</label>
                                                    <input
                                                        type="text"
                                                        name="address2"
                                                        value={businessFormData.address2}
                                                        onChange={handleBusinessFormChange}
                                                        style={styles.input}
                                                        placeholder="Enter address line 2"
                                                    />
                                                </div>

                                                <div style={styles.gridRow}>
                                                    <div style={styles.inputGroup}>
                                                        <label style={styles.label}>City</label>
                                                        <input
                                                            type="text"
                                                            name="city"
                                                            value={businessFormData.city}
                                                            onChange={handleBusinessFormChange}
                                                            style={styles.input}
                                                            placeholder="Enter city"
                                                        />
                                                    </div>

                                                    <div style={styles.inputGroup}>
                                                        <label style={styles.label}>State</label>
                                                        <input
                                                            type="text"
                                                            name="state"
                                                            value={businessFormData.state}
                                                            onChange={handleBusinessFormChange}
                                                            style={styles.input}
                                                            placeholder="Enter state"
                                                        />
                                                    </div>
                                                </div>

                                                <div style={styles.inputGroup}>
                                                    <label style={styles.label}>Pincode</label>
                                                    <input
                                                        type="text"
                                                        name="pincode"
                                                        value={businessFormData.pincode}
                                                        onChange={handleBusinessFormChange}
                                                        style={styles.input}
                                                        placeholder="Enter pincode"
                                                    />
                                                </div>
                                            </div>

                                            <div style={modalStyles.modalButtons}>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddBusinessModal(false)}
                                                    style={modalStyles.cancelButton}
                                                    disabled={addingBusiness}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={addBusiness}
                                                    disabled={addingBusiness}
                                                    style={{
                                                        ...modalStyles.submitButton,
                                                        ...(addingBusiness ? modalStyles.disabledButton : {})
                                                    }}
                                                >
                                                    {addingBusiness ? 'Adding...' : 'Add Business'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* </div> */}

                                {/* Map Section */}
                                <div style={styles.mapContainer}>
                                    <div style={styles.mapSection}>
                                        <div style={styles.mapHeader}>
                                            <h3 style={styles.mapTitle}>Select Location on Map</h3>
                                            <button
                                                type="button"
                                                onClick={getCurrentLocation}
                                                style={styles.locationButton}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                                            >
                                                📍 Use My Location
                                            </button>
                                        </div>

                                        <LoadScript
                                            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                            libraries={['places']}
                                        >
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                center={center}
                                                zoom={13}
                                                options={mapOptions}
                                                onClick={handleMapClick}
                                                onLoad={onMapLoad}
                                            >
                                                {markerPosition && (
                                                    <Marker
                                                        position={markerPosition}
                                                        title="Selected Location"
                                                    />
                                                )}
                                            </GoogleMap>
                                        </LoadScript>

                                        <p style={styles.mapInstructions}>
                                            Click on the map to select a location, or use "Use My Location" button to get your current position.
                                        </p>
                                    </div>
                                </div>

                                {/* Location Information */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        Location *
                                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                                            (Type address to auto-fill coordinates)
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Coimbatore, Tamil Nadu, India"
                                        style={styles.input}
                                        required
                                    />
                                    <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                        💡 Tip: Type an address and coordinates will be filled automatically
                                    </p>
                                </div>

                                <div style={styles.gridRow}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>
                                            Latitude *
                                        </label>
                                        <input
                                            type="text"
                                            name="latitude"
                                            value={formData.latitude}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 10.895150523154381"
                                            style={styles.input}
                                            required
                                        />
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>
                                            Longitude *
                                        </label>
                                        <input
                                            type="text"
                                            name="longitude"
                                            value={formData.longitude}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 76.9560718908906"
                                            style={styles.input}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Content */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        Text Content
                                    </label>
                                    <textarea
                                        name="text"
                                        value={formData.text}
                                        onChange={handleInputChange}
                                        rows={4}
                                        placeholder="Enter your text content here..."
                                        style={styles.textarea}
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        Geotag Description
                                    </label>
                                    <textarea
                                        name="geotags_description"
                                        value={formData.geotags_description}
                                        onChange={handleInputChange}
                                        rows={3}
                                        placeholder="e.g., Redeem instruction"
                                        style={styles.textarea}
                                    />
                                </div>

                                {/* Geotag Type */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        Geotag Type
                                    </label>
                                    <select
                                        name="geotag_type"
                                        value={formData.geotag_type}
                                        onChange={handleInputChange}
                                        style={styles.select}
                                    >
                                        <option value="Yourself">Yourself</option>
                                        <option value="Business">Business</option>
                                    </select>
                                </div>

                                {/* Logo Upload */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>
                                        Geotag Logo
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        style={styles.fileInput}
                                    />
                                    {geotagLogo && (
                                        <div style={styles.logoPreview}>
                                            <img src={geotagLogo} alt="Logo preview" style={styles.logoImage} />
                                        </div>
                                    )}
                                </div>

                                {/* File Uploads */}
                                <div style={styles.mediaSection}>
                                    <h3 style={styles.sectionTitle}>Media Files</h3>

                                    {/* Image Upload */}
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>
                                            Upload Images
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={(e) => handleFileUpload(e, 'image')}
                                            style={styles.fileInput}
                                        />
                                    </div>

                                    {/* Video Upload */}
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>
                                            Upload Videos
                                        </label>
                                        <input
                                            type="file"
                                            accept="video/*"
                                            multiple
                                            onChange={(e) => handleFileUpload(e, 'video')}
                                            style={styles.fileInput}
                                        />
                                    </div>

                                    {/* Audio Upload */}
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>
                                            Upload Audio
                                        </label>
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            onChange={(e) => handleFileUpload(e, 'audio')}
                                            style={styles.fileInput}
                                        />
                                    </div>
                                </div>

                                {/* Upload Progress */}
                                {uploading && (
                                    <div>
                                        <div style={styles.progressContainer}>
                                            <div
                                                style={{
                                                    ...styles.progressBar,
                                                    width: `${uploadProgress * 100}%`
                                                }}
                                            ></div>
                                        </div>
                                        <p style={styles.progressText}>Uploading... {Math.round(uploadProgress * 100)}%</p>
                                    </div>
                                )}

                                {/* Uploaded Files List */}
                                {files.length > 0 && (
                                    <div>
                                        <h4 style={styles.sectionTitle}>Uploaded Files</h4>
                                        <div style={styles.filesList}>
                                            {files.map((file, index) => (
                                                <div key={index} style={styles.fileItem}>
                                                    <div style={styles.fileInfo}>
                                                        <p style={styles.fileType}>
                                                            {file.file_type.charAt(0).toUpperCase() + file.file_type.slice(1)} File
                                                        </p>
                                                        <input
                                                            type="text"
                                                            placeholder="Add caption..."
                                                            value={file.caption}
                                                            onChange={(e) => updateFileCaption(index, e.target.value)}
                                                            style={styles.captionInput}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        style={styles.removeButton}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div style={styles.submitContainer}>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading || uploading}
                                        style={{
                                            ...styles.submitButton,
                                            ...(loading || uploading ? styles.submitButtonDisabled : styles.submitButtonEnabled)
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loading && !uploading) {
                                                e.target.style.backgroundColor = '#1d4ed8';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!loading && !uploading) {
                                                e.target.style.backgroundColor = '#2563eb';
                                            }
                                        }}
                                    >
                                        {loading ? 'Dropping Geotag...' : 'Drop Geotag'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>

    );
};

export default DropGeotagForm;